import { UserManager, WebStorageStateStore, Log } from 'oidc-client';
import { ApplicationPaths, ApplicationName } from './ApiAuthorizationConstants';

export class AuthorizeService {
    _callbacks = [];
    _nextSubscriptionId = 0;
    _user = null;
    _isAuthenticated = false;

    constructor() {
        Log.logger = console;
        Log.level = Log.ERROR;
    }


    // By default pop ups are disabled because they don't work properly on Edge.
    // If you want to enable pop up authentication simply set this flag to false.
    _popUpDisabled = true;

    async isAuthenticated() {
        const user = await this.getUser();
        return !!user;
    }

    async getUser() {
        if (this._user && this._user.profile) {
            return this._user.profile;
        }

        await this.ensureUserManagerInitialized();
        const user = await this.userManager.getUser();
        return user && user.profile;
    }

    async getAccessToken() {
        await this.ensureUserManagerInitialized();
        const user = await this.userManager.getUser();
        if (user && user.expired) {
            const renewedUser = await this.renewAccessToken();
            return renewedUser?.access_token;
        }
        return user && user.access_token;
    }

    // Avisa cada vez que oidc-client carga un usuario (login o renovación del token).
    // Devuelve la función para desuscribirse.
    async onUserLoaded(callback) {
        await this.ensureUserManagerInitialized();
        this.userManager.events.addUserLoaded(callback);
        return () => this.userManager.events.removeUserLoaded(callback);
    }

    // Cooldown tras un fallo de renovación: si signinSilent() viene fallando (ej. el
    // endpoint de discovery del Identity.API rechazado por CORS), evita que cada llamada
    // a getAccessToken()/401-retry dispare un signinSilent() nuevo de inmediato.
    _renewCooldownMs = 15000;
    _lastRenewFailureAt = 0;

    // Deduplica renovaciones concurrentes: si varias llamadas a getAccessToken() (o el
    // retry del interceptor de axios) piden renovar al mismo tiempo, todas esperan la
    // misma promesa de signinSilent en vez de disparar cada una la suya. Llamadas
    // concurrentes a signinSilent() en oidc-client suelen pisarse entre sí (iframe/state)
    // y fallar aunque la sesión siga siendo válida.
    async renewAccessToken() {
        if (!this._renewSilentPromise && (Date.now() - this._lastRenewFailureAt) < this._renewCooldownMs) {
            return null;
        }

        if (!this._renewSilentPromise) {
            this._renewSilentPromise = this.userManager.signinSilent()
                .then((user) => {
                    this._lastRenewFailureAt = 0;
                    return user;
                })
                .catch((e) => {
                    console.log('Error renovando token silenciosamente:', e?.message || e);
                    this._lastRenewFailureAt = Date.now();
                    return null;
                })
                .finally(() => {
                    this._renewSilentPromise = null;
                });
        }
        return this._renewSilentPromise;
    }

    // We try to authenticate the user in three different ways:
    // 1) We try to see if we can authenticate the user silently. This happens
    //    when the user is already logged in on the IdP and is done using a hidden iframe
    //    on the client.
    // 2) We try to authenticate the user using a PopUp Window. This might fail if there is a
    //    Pop-Up blocker or the user has disabled PopUps.
    // 3) If the two methods above fail, we redirect the browser to the IdP to perform a traditional
    //    redirect flow.
    async signIn(state) {
        await this.ensureUserManagerInitialized();
        try {
            const silentUser = await this.userManager.signinSilent(this.createArguments());
            this.updateState(silentUser);
            return this.success(state);
        } catch (silentError) {
            // User might not be authenticated, fallback to popup authentication
            //console.log("Silent authentication error: ", silentError);

            try {
                await this.userManager.signinRedirect(this.createArguments(state));
                return this.redirect();
            } catch (redirectError) {
                console.log("Redirect authentication error: ", redirectError);
                return this.error(redirectError);
            }
        }
    }

    async completeSignIn(url) {
        try {
            await this.ensureUserManagerInitialized();
            const user = await this.userManager.signinCallback(url);
            
            this.updateState(user);

            if (user && !user.profile.identification_number)
                return this.redirectToProfile(user.profile)
            else
                return this.success(user && user.state);
        } catch (error) {
            console.log('There was an error signing in: ', JSON.stringify(error));
            return this.error('');
        }
    }

    // We try to sign out the user in two different ways:
    // 1) We try to do a sign-out using a PopUp Window. This might fail if there is a
    //    Pop-Up blocker or the user has disabled PopUps.
    // 2) If the method above fails, we redirect the browser to the IdP to perform a traditional
    //    post logout redirect flow.
    async signOut(state) {
        await this.ensureUserManagerInitialized();
        try {
            if (this._popUpDisabled) {
                throw new Error('Popup disabled. Change \'AuthorizeService.js:AuthorizeService._popupDisabled\' to false to enable it.')
            }

            await this.userManager.signoutPopup(this.createArguments());
            this.updateState(undefined);
            return this.success(state);
        } catch (popupSignOutError) {
            console.log("Popup signout error: ", popupSignOutError);
            try {
                await this.userManager.signoutRedirect(this.createArguments(state));
                return this.redirect();
            } catch (redirectSignOutError) {
                console.log("Redirect signout error: ", redirectSignOutError);
                return this.error(redirectSignOutError);
            }
        }
    }

    async completeSignOut(url) {
        await this.ensureUserManagerInitialized();
        try {
            const response = await this.userManager.signoutCallback(url);
            this.updateState(null);
            return this.success(response && response.data);
        } catch (error) {
            console.log(`There was an error trying to log out '${error}'.`);
            return this.error(error);
        }
    }

    updateState(user) {
        this._user = user;
        this._isAuthenticated = !!this._user;
        this.notifySubscribers();
    }

    subscribe(callback) {
        this._callbacks.push({ callback, subscription: this._nextSubscriptionId++ });
        return this._nextSubscriptionId - 1;
    }

    unsubscribe(subscriptionId) {
        const subscriptionIndex = this._callbacks
            .map((element, index) => element.subscription === subscriptionId ? { found: true, index } : { found: false })
            .filter(element => element.found === true);
        if (subscriptionIndex.length !== 1) {
            // throw new Error(`Found an invalid number of subscriptions ${subscriptionIndex.length}`);
            console.log(`Found an invalid number of subscriptions ${subscriptionIndex.length}`);
            return;
        }

        this._callbacks = this._callbacks.splice(subscriptionIndex[0].index, 1);
    }

    notifySubscribers() {
        for (let i = 0; i < this._callbacks.length; i++) {
            const callback = this._callbacks[i].callback;
            callback();
        }
    }

    createArguments(state) {
        return { useReplaceToNavigate: true, data: state };
    }

    error(message) {
        return { status: AuthenticationResultStatus.Fail, message };
    }

    success(state) {
        return { status: AuthenticationResultStatus.Success, state };
    }

    redirect() {
        return { status: AuthenticationResultStatus.Redirect };
    }

    redirectToProfile(user) {
        return { status: AuthenticationResultStatus.RedirectToProfile, user };
    }

    async ensureUserManagerInitialized() {
        if (this.userManager !== undefined) {
            return;
        }

        let response = await fetch(ApplicationPaths.ApiAuthorizationClientConfigurationUrl);
        if (!response.ok) {
            throw new Error(`Could not load settings for '${ApplicationName}'`);
        }

        let publicSettings = await response.json();
        //console.log(publicSettings)
        let settings = {
            client_id: "WebReact",
            authority: publicSettings?.identityApi,
            automaticSilentRenew: true,
            includeIdTokenInSilentRenew: true,
            userStore: new WebStorageStateStore({
                prefix: ApplicationName
            }),
            clockSkew: 900,
            redirect_uri: `${publicSettings?.callBackUrl}/authentication/login-callback`,
            post_logout_redirect_uri: `${publicSettings?.callBackUrl}/authentication/logout-callback`,
            response_type: "code",
            scope: "openid profile IdentityServerApi protocolsapi databaseapi agendaapi notificationsapi medicalrecordapi digitalsignapi WebReact medicalinsuranceagreementsapi pricelistapi pre_biller_app inventoryapi alphabetaapi",
        };

        this.userManager = new UserManager(settings);

        this.userManager.events.addUserSignedOut(async () => {
            await this.userManager.removeUser();
            this.updateState(undefined);
        });

        // Guarda contra el auto-renew interno de oidc-client: si el access token ya está
        // vencido, AccessTokenEvents.load() rearma el timer de "expiring" en 1 segundo cada
        // vez, así que un signinSilent() que sigue fallando (ej. bug de CORS en el discovery
        // endpoint del Identity.API) dispara un loop apretado indefinido. Si falla varias
        // veces seguidas, frenamos ese timer automático; getAccessToken()/el interceptor de
        // axios (con su propio cooldown) siguen pudiendo renovar bajo demanda.
        this._autoRenewFailureCount = 0;
        this.userManager.events.addSilentRenewError((e) => {
            this._autoRenewFailureCount++;
            console.log(`Fallo de renovación automática (${this._autoRenewFailureCount}):`, e?.message || e);
            if (this._autoRenewFailureCount >= 3) {
                console.log('Demasiados fallos de renovación automática seguidos, deteniendo automaticSilentRenew.');
                this.userManager.stopSilentRenew();
            }
        });
        this.userManager.events.addUserLoaded(() => {
            if (this._autoRenewFailureCount >= 3) {
                this.userManager.startSilentRenew();
            }
            this._autoRenewFailureCount = 0;
        });
    }

    static get instance() { return authService }
}

const authService = new AuthorizeService();

export default authService;

export const AuthenticationResultStatus = {
    Redirect: 'redirect',
    Success: 'success',
    Fail: 'fail',
    RedirectToProfile: 'redirectToProfile'
};
